import { useDispatch, useSelector } from 'react-redux';

import { DiffMetadataProps } from 'src/components/diff-set/diff-set';
import { showFlagComponent } from 'src/redux/flags';
import { EXPAND_CONTEXT } from 'src/redux/pull-request/actions';
import { convertToChunksFormat } from 'src/redux/pull-request/sagas/utils/convert-to-chunks-format';
import { getContextDataTopicDiff } from 'src/redux/pull-request/sagas/utils/get-context-data';
import { getAllDiffFiles } from 'src/redux/pull-request/selectors';
import { getDiffPath } from 'src/redux/pull-request/utils/get-diff-path';
import { Diff } from 'src/types/pull-request';
import authRequest from 'src/utils/fetch';
import {
  ContextLineRanges,
  validateContextLineRanges,
} from 'src/utils/validate-context-lines';

import { useBranchUrls } from '../urls';

export type ContextExpansionParams = {
  file: Diff;
  filepath: string;
  diffMetadataProps: DiffMetadataProps;
};

export const useContextExpansion = ({
  file,
  filepath,
  diffMetadataProps,
}: ContextExpansionParams): {
  onExpand: (
    expanderIndex: number,
    isShiftClicked: boolean,
    shouldExpandDown: boolean
  ) => Promise<void>;
} => {
  const dispatch = useDispatch();
  const { chunks } = file;

  const { sourceHash } = diffMetadataProps;

  const { sourceApiUrl } = useBranchUrls();
  const diffs: Diff[] = useSelector(getAllDiffFiles);
  const indexFromFullDiff = diffs.findIndex(
    diff => getDiffPath(diff) === filepath
  );

  const onExpand = async (
    expanderIndex: number,
    isShiftClicked: boolean,
    shouldExpandDown: boolean
  ) => {
    try {
      const contextData = getContextDataTopicDiff(
        chunks,
        expanderIndex,
        isShiftClicked,
        shouldExpandDown
      );

      try {
        validateContextLineRanges(contextData.lineNums);
      } catch {
        // Only logging these in Sentry for now
      }

      const url = sourceApiUrl(
        sourceHash,
        filepath,
        contextData.lineNums as ContextLineRanges
      );

      const response = await fetch(authRequest(url));

      if (response.ok) {
        const resp = await response.json();

        // remove the trailing new line
        const lines = (resp.data && resp.data.split('\n').slice(0, -1)) || [];
        const { chunkId, beforeOrAfter } = contextData;
        const lineNums: ContextLineRanges =
          contextData.lineNums as ContextLineRanges;
        const convertedJson = convertToChunksFormat(lines, lineNums);

        dispatch({
          type: EXPAND_CONTEXT.SUCCESS,
          payload: {
            ...convertedJson,
            fileIndex: indexFromFullDiff,
            chunkId,
            beforeOrAfter,
          },
        });
      } else if (response.status === 403 || response.status === 404) {
        dispatch(showFlagComponent('load-context-forbidden-not-found'));
        dispatch({
          type: EXPAND_CONTEXT.ERROR,
          payload: {
            fileIndex: indexFromFullDiff,
          },
          error: true,
        });
      } else {
        dispatch(showFlagComponent('load-context-error'));
        dispatch({
          type: EXPAND_CONTEXT.ERROR,
          payload: {
            fileIndex: indexFromFullDiff,
          },
          error: true,
        });
      }
    } catch (error) {
      dispatch(showFlagComponent('load-context-error'));
      dispatch({
        type: EXPAND_CONTEXT.ERROR,
        payload: {
          message: error.message,
          fileIndex: indexFromFullDiff,
        },
        error: true,
      });
    }
  };

  return { onExpand };
};
